import { YesNo } from './PZSESchema'
import type { LabelValueOption, UltraRailRailType } from './UltraRailTypes'

export type { UltraRailRailType } from './UltraRailTypes'

type RailTypeRow = {
  profile: UltraRailRailType
  finish: string
  lengthInches: number
  sku: string
  isUsa: boolean
}

/**
 * Legacy UR-40 / UR-60 SKUs — discontinued per SnapNRack; kept for saved-project lookup only.
 * Not included in `RAIL_TYPE_ROWS` or `getRailTypeOptions()`.
 */
const DISCONTINUED_RAIL_TYPE_ROWS: ReadonlyArray<RailTypeRow> = [
  { profile: 'UR-40', finish: 'Mill', lengthInches: 172, sku: '232-02536', isUsa: false },
  { profile: 'UR-40', finish: 'Silver', lengthInches: 172, sku: '232-02537', isUsa: false },
  { profile: 'UR-40', finish: 'Black', lengthInches: 172, sku: '232-02538', isUsa: false },
  { profile: 'UR-40', finish: 'Mill', lengthInches: 86, sku: '232-02550', isUsa: false },
  { profile: 'UR-60', finish: 'Mill', lengthInches: 172, sku: '232-02539', isUsa: false },
  { profile: 'UR-60', finish: 'Silver', lengthInches: 172, sku: '232-02540', isUsa: false },
  { profile: 'UR-60', finish: 'Black', lengthInches: 172, sku: '232-02541', isUsa: false },
]

// UR-40 and UR-60 removed from selectable rail types (discontinued per SnapNRack):
// { profile: 'UR-40', finish: 'Mill', lengthInches: 172, sku: '232-02536', isUsa: false },
// { profile: 'UR-40', finish: 'Silver', lengthInches: 172, sku: '232-02537', isUsa: false },
// { profile: 'UR-40', finish: 'Black', lengthInches: 172, sku: '232-02538', isUsa: false },
// { profile: 'UR-40', finish: 'Mill', lengthInches: 86, sku: '232-02550', isUsa: false },
// { profile: 'UR-60', finish: 'Mill', lengthInches: 172, sku: '232-02539', isUsa: false },
// { profile: 'UR-60', finish: 'Silver', lengthInches: 172, sku: '232-02540', isUsa: false },
// { profile: 'UR-60', finish: 'Black', lengthInches: 172, sku: '232-02541', isUsa: false },

const RAIL_TYPE_ROWS: ReadonlyArray<RailTypeRow> = [
  { profile: 'UR-45', finish: 'Black', lengthInches: 172, sku: '232-10096', isUsa: false },
  { profile: 'UR-45', finish: 'Black (USA)', lengthInches: 172, sku: '232-10096-USA', isUsa: true },
  { profile: 'UR-45', finish: 'Mill', lengthInches: 94, sku: '232-10097', isUsa: false },
  { profile: 'UR-45', finish: 'Mill (USA)', lengthInches: 172, sku: '232-10095-USA', isUsa: true },
  { profile: 'UR-45', finish: 'Mill (USA)', lengthInches: 94, sku: '232-10097-USA', isUsa: true },
  { profile: 'UR-45', finish: 'Black', lengthInches: 94, sku: '232-10098', isUsa: false },
  { profile: 'UR-45', finish: 'Mill (USA)', lengthInches: 186, sku: '232-10130-USA', isUsa: true },
]

function findRailTypeRow(sku: string): RailTypeRow | undefined {
  return RAIL_TYPE_ROWS.find((r) => r.sku === sku) ?? DISCONTINUED_RAIL_TYPE_ROWS.find((r) => r.sku === sku)
}

/**
 * Rail type dropdown options. Label format: "${Profile} ${Finish}, ${Length}\"".
 * When prioritiseDomesticContent is 'Yes', only options with SKU ending in -USA are returned.
 * UR-40 and UR-60 are excluded (discontinued per SnapNRack).
 */
export function getRailTypeOptions(prioritiseDomesticContent?: YesNo): LabelValueOption[] {
  const filtered = prioritiseDomesticContent === 'Yes' ? RAIL_TYPE_ROWS.filter((r) => r.isUsa) : RAIL_TYPE_ROWS
  return filtered.map((r) => ({
    value: r.sku,
    label: `${r.profile} ${r.finish}, ${r.lengthInches}"`,
  }))
}

/** Include a discontinued rail SKU in options when a saved project still references it. */
export function getRailTypeOptionsForForm(
  prioritiseDomesticContent?: YesNo,
  currentSku?: string
): LabelValueOption[] {
  const options = getRailTypeOptions(prioritiseDomesticContent)
  if (!currentSku || options.some((o) => o.value === currentSku)) {
    return options
  }
  const row = DISCONTINUED_RAIL_TYPE_ROWS.find((r) => r.sku === currentSku)
  if (!row) {
    return options
  }
  return [
    {
      value: row.sku,
      label: `${row.profile} ${row.finish}, ${row.lengthInches}" (discontinued)`,
    },
    ...options,
  ]
}

/** Map rail type (SKU) to length in inches for BOM/calculations. */
export function getRailLengthInchesForRailTypeCode(sku: string | undefined): number | null {
  if (!sku) return null
  const row = findRailTypeRow(sku)
  return row ? row.lengthInches : null
}

/** Derive profile (UR-45, or legacy UR-40/UR-60) from rail component code. For PZSE and splice logic. */
export function getProfileFromRailTypeCode(sku: string | undefined): UltraRailRailType | null {
  if (!sku) return null
  const row = findRailTypeRow(sku)
  return row ? row.profile : null
}

/**
 * Returns splice connector finish dropdown options based on rail type and USA preference.
 * UR-45 (and legacy UR-40) share splice connectors; UR-60 has its own (no USA variants).
 */
export function getSpliceFinishOptions(
  railType: UltraRailRailType,
  prioritiseDomesticContent?: YesNo
): LabelValueOption[] {
  if (railType === 'UR-60') {
    return [
      { value: 'Silver', label: 'Silver' },
      { value: 'Black', label: 'Black' },
    ]
  }

  if (prioritiseDomesticContent === 'Yes') {
    return [
      { value: 'Silver-USA', label: 'Silver' },
      { value: 'Black-USA', label: 'Black' },
    ]
  }

  return [
    { value: 'Silver', label: 'Silver' },
    { value: 'Black', label: 'Black' },
  ]
}

export function getSpliceCodeForFinish(railType: UltraRailRailType, spliceFinishValue: string): string | null {
  if (railType === 'UR-60') {
    switch (spliceFinishValue) {
      case 'Silver':
        return '242-01271'
      case 'Black':
        return '242-01270'
      default:
        return null
    }
  }
  // UR-45 and legacy UR-40 share the same splice connectors
  switch (spliceFinishValue) {
    case 'Silver':
      return '242-01213'
    case 'Black':
      return '242-01214'
    case 'Silver-USA':
      return '242-01213-USA'
    case 'Black-USA':
      return '242-01214-USA'
    default:
      return null
  }
}

export function getEndClampOptions(prioritiseDomesticContent?: YesNo): LabelValueOption[] {
  if (prioritiseDomesticContent === 'Yes') {
    return [
      { value: '242-02215-USA', label: 'Universal End Clamp (USA)' },
      { value: '242-02073-USA', label: 'Ultra Rail End Clamp, Black (USA)' },
    ]
  }
  return [
    { value: '242-02215', label: 'Universal End Clamp' },
    { value: '242-02215-USA', label: 'Universal End Clamp (USA)' },
    { value: '242-02073', label: 'Ultra Rail End Clamp, Black' },
    { value: '242-02073-USA', label: 'Ultra Rail End Clamp, Black (USA)' },
    { value: '242-02072', label: 'Ultra Rail End Clamp, Mill' },
  ]
}

export function getMidClampOptions(prioritiseDomesticContent?: YesNo): LabelValueOption[] {
  if (prioritiseDomesticContent === 'Yes') {
    return [{ value: '242-02071-USA', label: 'Ultra Rail Mid Clamp, Black (USA)' }]
  }
  return [
    { value: '242-02071', label: 'Ultra Rail Mid Clamp, Black' },
    { value: '242-02071-USA', label: 'Ultra Rail Mid Clamp, Black (USA)' },
    { value: '242-02070', label: 'Ultra Rail Mid Clamp, Silver' },
  ]
}
